import * as React from 'react';
import { configureAuth, getCurrentUserSafe, signOut as doSignOut } from './auth';

export interface CurrentUser {
  sub: string;
  email: string;
  groups: string[];
  isAdmin: boolean;
  isGuildMember: boolean;
}

interface AuthContextType {
  user: CurrentUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthCtx = React.createContext<AuthContextType>({
  user: null,
  loading: true,
  refresh: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<CurrentUser | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const u = await getCurrentUserSafe();
    setUser(u);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    configureAuth();
    void refresh();
  }, [refresh]);

  const signOut = React.useCallback(async () => {
    await doSignOut();
    setUser(null);
  }, []);

  return <AuthCtx.Provider value={{ user, loading, refresh, signOut }}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return React.useContext(AuthCtx);
}
